import React from 'react';
import { Download } from 'lucide-react';
import { formatCurrency, formatNumberNoDecimals } from '../utils/numberUtils';
import Button from './Button';

const PercentageTaxSummary = ({ selectedPeriod, periodSummary, annualSummary, onExportCSV, onExportSummary }) => {
  const isAllPeriods = !selectedPeriod?.quarter && !selectedPeriod?.year;

  return (
    <div className="space-y-6">
      {/* Period Summary Card */}
      <div className="bg-white border border-ink-line rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h4 className="text-lg font-medium text-ink">Current period summary</h4>
            <p className="text-sm text-ink-body">
              {isAllPeriods
                ? 'All periods combined'
                : selectedPeriod?.quarter && selectedPeriod?.year
                ? `Quarter ${selectedPeriod.quarter}, ${selectedPeriod.year}`
                : selectedPeriod?.year
                ? `Fiscal Year ${selectedPeriod.year}`
                : 'Filtered Period'}
            </p>
          </div>

          <div className="flex gap-2">
            <Button type="button" onClick={onExportCSV} variant="outline" size="sm" icon={Download}>
              Export data
            </Button>
            <Button type="button" onClick={onExportSummary} variant="outline" size="sm" icon={Download}>
              Export summary
            </Button>
          </div>
        </div>

        {/* explanatory formula */}
        <p className="text-xs text-ink-body font-mono">
          Sales (Tax due from sales tab) − Income − Form 2306 − Excess = Percentage Tax Payable
        </p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-3">
          <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
            <div className="text-xs text-ink-body">
              Sales (tax due from sales tab)
            </div>
            <div className="text-lg font-medium text-ink tabular-nums">
              {formatCurrency(periodSummary?.salesTaxDue || 0)}
            </div>
          </div>

          <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
            <div className="text-xs text-ink-body">
              Income
            </div>
            <div className="text-lg font-medium text-ink tabular-nums">
              {formatCurrency(periodSummary?.incomeTaxDue || 0)}
            </div>
          </div>

          <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
            <div className="text-xs text-ink-body">
              Form 2306 total
            </div>
            <div className="text-lg font-medium text-ink tabular-nums">
              {formatCurrency(periodSummary?.total2306 || 0)}
            </div>
          </div>

          <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
            <div className="text-xs text-ink-body">
              Excess from previous quarter
            </div>
            <div className="text-lg font-medium text-ink tabular-nums">
              {formatCurrency(periodSummary?.excessFromPrevQuarter || 0)}
            </div>
          </div>

          <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
            <div className="text-xs text-primary-700">
              Percentage tax payable
            </div>
            <div className="text-lg font-medium text-primary-700 tabular-nums">
              {formatCurrency(periodSummary?.netTaxPayable || 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Annual Summary */}
      {Array.isArray(annualSummary) && annualSummary.length > 0 && (
        <div className="border border-ink-line rounded-lg">
          <div className="border-b border-ink-line p-4">
            <h4 className="font-medium text-ink">Annual summary</h4>
            <p className="text-sm text-ink-body">Year-over-year percentage tax totals</p>
          </div>

          <div className="p-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-line">
              <thead className="bg-ink-canvas">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">Year</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">Records</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">Total Amount</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">Total Tax Due</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">Q1</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">Q2</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">Q3</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">Q4</th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-ink-line">
                {annualSummary.map((y) => (
                  <tr key={y.year}>
                    <td className="px-3 py-2 text-sm font-medium text-ink">{y.year}</td>
                    <td className="px-3 py-2 text-sm text-ink-body">{formatNumberNoDecimals(y.recordCount || 0)}</td>
                    <td className="px-3 py-2 text-sm text-ink-body tabular-nums">{formatCurrency(y.totalAmount || 0)}</td>
                    <td className="px-3 py-2 text-sm font-medium text-primary-700 tabular-nums">{formatCurrency(y.totalTaxDue || 0)}</td>
                    <td className="px-3 py-2 text-sm text-ink-body tabular-nums">{formatCurrency(y.quarters?.[1] || 0)}</td>
                    <td className="px-3 py-2 text-sm text-ink-body tabular-nums">{formatCurrency(y.quarters?.[2] || 0)}</td>
                    <td className="px-3 py-2 text-sm text-ink-body tabular-nums">{formatCurrency(y.quarters?.[3] || 0)}</td>
                    <td className="px-3 py-2 text-sm text-ink-body tabular-nums">{formatCurrency(y.quarters?.[4] || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PercentageTaxSummary;